// The language the product dialog edits in — one for the whole dialog, picked
// at its top. Every text a product says in a language follows it: the title,
// the description and the fields, the pictures, the badges, the options'
// labels; a picture uploaded goes into it. What is the same in every language
// (a price, a stock) does not change with it, and the fields that do carry a
// small tag with the language's code (LanguageTag), so it is plain which is which.
//
// The products page holds the state (useEditingLanguageState) and hands it
// down with EditingLanguageProvider; the content editor gets it as its
// `language` too, so a section that only reads it needs nothing more. A tab
// that also switches it (a scene editor of its own) takes useEditingLanguage().
import React, { createContext, useContext, useEffect, useMemo, useState } from "react"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Tooltip from "aldoo-ra/Tooltip"
import { LanguageIcon } from "@heroicons/react/24/outline"
import { useStoreLanguages } from "aldoo-ra/CMS/field-editors/store-option-values"

const EditingLanguageContext = createContext(null)

/** `{ language, setLanguage, languages, primary }`, or null outside the product dialog. */
export const useEditingLanguage = () => useContext(EditingLanguageContext)

export const EditingLanguageProvider = ({ value, children }) => <EditingLanguageContext.Provider value={value}>{children}</EditingLanguageContext.Provider>

/**
 * The dialog's language state. It opens on `preferred` when the store has it (a
 * host's choice — its sellers write in Bulgarian first), else on the product's
 * own first language (a Bulgarian shop's product in Bulgarian), else on the
 * site's primary one, and comes back to that whenever another product is opened.
 */
export function useEditingLanguageState(product, { preferred: wanted = null } = {}) {
  const languages = useStoreLanguages()
  const primary = languages[0]?.code || "en"
  const productLanguage = Array.isArray(product?.languages) ? product.languages.find((code) => languages.some((l) => l.code === code)) : null
  const preferred = wanted && languages.some((l) => l.code === wanted) ? wanted : null
  const opening = preferred || productLanguage || primary
  const [language, setLanguage] = useState(opening)
  // another product, or the list of languages arriving: start over on its language
  useEffect(() => {
    setLanguage(opening)
  }, [product?._id, opening])
  return useMemo(() => ({ language, setLanguage, languages, primary }), [language, languages, primary])
}

const nameOf = (l) => `${l.displayName || l.name || l.code} (${String(l.code).toUpperCase()})`

/** The picker at the top of the dialog (a header action of the content editor). */
export function EditingLanguageSwitch() {
  const state = useEditingLanguage()
  const t = useProductEditorTexts()
  if (!state || state.languages.length < 2) return null
  const { language, setLanguage, languages } = state
  const options = languages.map((l) => ({ value: l.code, name: nameOf(l) }))
  return (
    <Tooltip content={t`The language the product's texts and pictures are edited in`} position="top">
      <div className="flex items-center gap-1.5 w-[200px]" data-testid="editing-language">
        <LanguageIcon className="w-5 h-5 shrink-0 text-admin_text_secondary" />
        <DropdownSelect
          variant="admin"
          options={options}
          value={[options.find((o) => o.value === language) || options[0]]}
          enableResetButton={false}
          showCheckbox={false}
          onChange={(picked) => {
            const option = Array.isArray(picked) ? picked[0] : picked
            if (option?.value) setLanguage(option.value)
          }}
        />
      </div>
    </Tooltip>
  )
}

// last among the header actions: the picker stays put beside the tools while an action
// that shows only for some languages (auto-translate) comes and goes on its left
EditingLanguageSwitch.last = true

/** The code of the language a field is edited in, beside its label: this one changes with the picker. */
export function LanguageTag({ language, className = "" }) {
  const state = useEditingLanguage()
  const code = language || state?.language
  if (!code) return null
  return (
    <span
      className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-semibold uppercase tracking-wide bg-admin_primary/10 text-admin_primary align-middle ${className}`}
      title="Edited in the language picked at the top of the dialog"
      data-testid="language-tag"
    >
      {code}
    </span>
  )
}
